// Focus trap helper for modal-like overlays. Cycles Tab / Shift+Tab
// across the focusable descendants of `container`, wrapping at the
// edges. Used by Modal + Drawer; Popover + Tooltip don't trap (they
// stay non-modal so the user can keep moving through the page).
//
// Returns a cleanup function the caller invokes on close. The trap
// also restores focus to the previously-focused element on cleanup.

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not(:disabled)",
  "textarea:not(:disabled)",
  "input:not(:disabled):not([type='hidden'])",
  "select:not(:disabled)",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export function trapFocus(container: HTMLElement): () => void {
  const previousActive = document.activeElement as HTMLElement | null;

  function focusables(): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => {
      // Visible + not aria-hidden. happy-dom doesn't compute layout
      // so offsetParent === null check (the usual visibility heuristic)
      // would false-positive in tests; rely on aria-hidden ancestors only.
      return el.closest("[aria-hidden='true']") === null;
    });
  }

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== "Tab") return;
    const items = focusables();
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (first === undefined || last === undefined) return;
    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (active === first || !container.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (active === last || !container.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  container.addEventListener("keydown", handleKeyDown);

  // Initial focus → first focusable inside the container.
  // Defer one tick so the container's children have mounted.
  // setTimeout(0) over queueMicrotask: microtasks fire BEFORE the
  // render commit in some test environments (happy-dom), so the
  // children we're trying to focus aren't in the DOM yet. A macrotask
  // gives React's commit a chance to run first.
  const focusTimer = setTimeout(() => {
    const items = focusables();
    if (items.length > 0) items[0]?.focus();
    else container.focus();
  }, 0);

  return () => {
    clearTimeout(focusTimer);
    container.removeEventListener("keydown", handleKeyDown);
    if (previousActive !== null && typeof previousActive.focus === "function") {
      previousActive.focus();
    }
  };
}
